import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { media, theme } from 'styles';
import map from 'images/testimonials/North-America-Map.png';
import HowardThomas from 'images/testimonials/HowardThomas.png';
import ShaoYang from 'images/testimonials/ShaoYang.png';
import placeholderImage from 'images/testimonials/biobelle-logo.png';
import {
  Floater,
  Container,
  SeoTags,
  SwipeableCaseStudies,
} from 'components/shared';
import { Hero, VideoCallout, ServiceDetails } from './components';
import FacebookTags from '../shared/FacebookTags/FacebookTags';

const { colors, sp } = theme;

const Wrapper = styled.div`
  width: 100%;
  overflow: hidden;
`;

const TestimonialsWrapper = styled.div`
  margin-top: ${sp.l};
  padding: 60px 0 0;

  ${media.tablet`
    background-image: url('${props => props.src}');
    background-size: contain;
    background-position: 50% 50%;
    background-repeat: no-repeat;
    padding: 120px 0 200px;
  `};
`;

const TestimonialsHeadings = styled.div`
  text-align: center;
  margin: 0 auto 100px;
  max-width: 600px;

  h3 {
    font-size: 36px;
    line-height: 42px;
    margin-bottom: ${sp.l};
  }

  h4 {
    font-size: 18px;
    line-height: 23px;
  }
`;

const TestimonialsGrid = styled.div`
  ${media.tablet`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
  `};
`;

// prettier-ignore
const TestimonialsItem = styled.div`
  margin: 0 0 40px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  color: ${colors.white};

  ${media.tablet`
    width: calc((100% / 3) - (80px / 3));
  `};

  &:nth-child(1) {
    background-color: ${colors.blue};

    ${media.tablet`
      margin: 40px 40px 0 0;
    `};
  }

  &:nth-child(2) {
    background-color: ${colors.navy};
    ${media.tablet`
      margin: 0 40px 0 0;
    `};
  }

  &:nth-child(3) {
    background-color: ${colors.royal};
    ${media.tablet`
      margin: 80px 0 0;
    `};
  }
`;

const TestimonialContent = styled.div`
  padding: 30px 30px 80px;
`;

const TestimonialMeta = styled.div`
  position: relative;
  text-align: right;
  padding: 30px;
  background-color: ${colors.white};
  color: ${colors.greyDark};

  h3 {
    display: inline-block;
    font-size: 16px;
    line-height: 18px;
    font-weight: 700;
    margin-bottom: ${sp.s};

    ${media.tablet`
      max-width: 100px;
    `};

    ${media.desktop`
      max-width: none;
    `};
  }

  h4 {
    font-size: 16px;
    line-height: 18px;
    font-weight: 500;
  }
`;

const TestimonialImage = styled.div`
  position: absolute;
  top: -50px;
  left: 30px;
  width: 100px;
  height: 100px;
  background-color: ${colors.mustard};
  border: 4px solid ${colors.white};
  border-radius: 50%;

  img {
    max-width: 100%;
    border-radius: 50%;
  }
`;

// prettier-ignore
const Section = styled.div`
  padding: 90px 0 100px;
  background: ${colors.grey};
  ${props => props.bg && `background: ${colors[props.bg]};`};

  ${media.laptop`
    padding-bottom: 160px;
    ${props => props.floaters && 'padding-bottom: 0;'};
    ${props => props.initial && `
      padding-bottom: 0px;
    `}
  `}

  ${props => props.floaters && `
    position: relative;
    padding-top: 0;
    padding-bottom: 0;
    height: 0;
    background: none;
    overflow: visible;
  `}
`;

const CaseStudiesHeadings = styled.div`
  text-align: center;
  margin: 0 auto 100px;
  max-width: 600px;

  h3 {
    font-size: 36px;
    line-height: 42px;
    margin-bottom: ${sp.l};
  }

  h4 {
    font-size: 18px;
    line-height: 23px;
  }
`;

const propTypes = {
  data: PropTypes.shape({ contentfulHomepage: PropTypes.object }).isRequired,
};

function HomePage({ data }) {
  const homepage = data.contentfulHomepage;
  const caseStudyNodes = data.allContentfulCaseStudy;
  const { seoTitle, seoKeywords, seoDescription } = homepage;

  return (
    <Wrapper>
      <SeoTags
        title={seoTitle}
        keywords={seoKeywords}
        description={seoDescription && seoDescription.seoDescription}
      />

      <FacebookTags
        fbUrl={`${process.env.GATSBY_MARKETTAP_SITE_HOST}/`}
        fbTitle={seoTitle}
        fbDescription={seoDescription && seoDescription.seoDescription}
        fbImage={`http:${homepage.heroImage.fullSize.src}`}
      />

      <Section bg="offWhite" initial>
        <Hero
          heading={homepage.heroHeading}
          image={homepage.heroImage}
          buttonText={homepage.heroButtonText}
          textLines={homepage.heroTypingValues}
        />
      </Section>

      <Section bg="white">
        <VideoCallout
          heading={homepage.videoHeading}
          subheading={homepage.videoSubheading}
          buttonText={homepage.videoButtonText}
          videoThumbnail={homepage.videoThumbnail}
        />
      </Section>

      <Section floaters>
        <Container>
          <Floater img="coffee" ratio={0.07} />
        </Container>
      </Section>

      <Section bg="white">
        <ServiceDetails details={homepage.serviceDetails} />
      </Section>

      <Section bg="blueIce">
        <Container>
          <CaseStudiesHeadings>
            <h3>{homepage.caseStudyHeading}</h3>
            <h4>{homepage.caseStudySubheading}</h4>
          </CaseStudiesHeadings>
          <SwipeableCaseStudies nodes={caseStudyNodes} />
        </Container>

        <TestimonialsWrapper src={map}>
          <Container>
            <TestimonialsHeadings>
              <h3>We work with awesome brands across North America.</h3>
              <h4>See what they’re saying about us.</h4>
            </TestimonialsHeadings>
            <TestimonialsGrid>
              <TestimonialsItem>
                <TestimonialContent>
                  MarketTap has all of the attributes you need to work
                  successfully these days – they &quot;get&quot; new marketing
                  and media paradigms and have a unique ability to blend the
                  science of media with arts to generate thoughtful results. The
                  great thing about Market Tap is not only their high level of
                  responsiveness but more important, a real sense of
                  pro-activity they bring to the relationship. It&apos;s great
                  to work with a team that gets ahead of the curve on a daily
                  basis and has a service orientation that is not just about
                  being fast, but adding value every day.
                </TestimonialContent>
                <TestimonialMeta>
                  <TestimonialImage>
                    <img
                      src={HowardThomas}
                      alt="Howard Thomas, CMO, U-Feast Corporation"
                    />
                  </TestimonialImage>
                  <h3>Howard Thomas</h3>
                  <h4>CMO, U-Feast Corporation</h4>
                </TestimonialMeta>
              </TestimonialsItem>
              <TestimonialsItem>
                <TestimonialContent>
                  MarketTap handles our SEO. We were absolutely thrilled to see
                  a 22% increase in website visitors in the first month of the
                  program. January is usually a really slow month for us, but we
                  were jammed with appointments made through our website. This
                  proved the importance of SEO and that MarketTap&apos;s
                  services really work.
                </TestimonialContent>
                <TestimonialMeta>
                  <TestimonialImage>
                    <img
                      src={ShaoYang}
                      alt="Shao Yang, Founder, The Tailory New York"
                    />
                  </TestimonialImage>
                  <h3>Shao Yang</h3>
                  <h4>Founder, The Tailory New York</h4>
                </TestimonialMeta>
              </TestimonialsItem>
              <TestimonialsItem>
                <TestimonialContent>
                  MarketTap&apos;s a la carte service model is so impressive.
                  They have helped us double our online sales with Facebook
                  Advertising and contests. Overtime, we began to outsource
                  other activities we find time consuming, like writing product
                  descriptions, creating newsletters, and monitoring our Google
                  Analytics. They are fast, smart, and really reliable.
                </TestimonialContent>
                <TestimonialMeta>
                  <TestimonialImage>
                    <img
                      src={placeholderImage}
                      alt="Forty Amsel, CEO, Biobelle Cosmetics"
                    />
                  </TestimonialImage>
                  <h3>Forty Amsel</h3>
                  <h4>CEO, Biobelle Cosmetics</h4>
                </TestimonialMeta>
              </TestimonialsItem>
            </TestimonialsGrid>
          </Container>
        </TestimonialsWrapper>

        {/* <Testimonials testimonials={homepage.testimonials} /> */}
      </Section>
    </Wrapper>
  );
}

HomePage.propTypes = propTypes;

export default HomePage;



// WEBPACK FOOTER //
// ./src/components/HomePage/HomePage.jsx